import { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { ChatHistorySidebar } from "@/components/assistant/ChatHistorySidebar"
import { ChatMessageArea } from "@/components/assistant/ChatMessageArea"
import { ChatInput } from "@/components/assistant/ChatInput"
import { ChatDocumentPanel } from "@/components/chat/ChatDocumentPanel"
import { ArtifactPanel } from "@/components/assistant/ArtifactPanel"
import { ChatErrorBanner } from "@/components/chat/ChatErrorBanner"
import { useAssistantChatLifecycle } from "@/hooks/useAssistantChatLifecycle"
import { useAssistantStore } from "@/stores/assistant-store"
import { usePreviewStore } from "@/stores/preview-store"
import { useDockHost, useDockedPreview, useWideViewport } from "@/hooks/useDockedPreview"
import { cn } from "@/lib/utils"
import { Loader2 } from "lucide-react"
import { useIsRoleLoaded, useIsViewOnly } from "@/hooks/useViewOnly"
import { ViewOnlyNotice } from "@/components/ViewOnlyNotice"

/** Shown instead of the conversation: every assistant chat needs a session the backend refuses a viewer. */
export const ASSISTANT_VIEW_ONLY_MESSAGE = "You have view-only access, so the assistant isn't available."

export function AssistantPage() {
  // A separate component, not an early return: the conversation's hooks start
  // a session on mount, and must not run for a view-only user at all.
  const isViewOnly = useIsViewOnly()
  // Until the role is known a viewer looks like a member; mounting the
  // conversation then would fire the session request that 403s.
  const isRoleLoaded = useIsRoleLoaded()
  if (!isRoleLoaded) {
    return (
      <div className="flex h-full items-center justify-center bg-card">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading" />
      </div>
    )
  }
  if (isViewOnly) {
    return (
      <div className="flex h-full items-center justify-center bg-card px-4">
        <ViewOnlyNotice message={ASSISTANT_VIEW_ONLY_MESSAGE} className="text-sm" />
      </div>
    )
  }
  return <AssistantConversationPage />
}

function AssistantConversationPage() {
  const { threadId } = useParams<{ threadId: string }>()
  const navigate = useNavigate()
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(true)
  // This page renders the docked reader, so chat previews may dock here.
  useDockHost('chat')
  const shouldDock = useDockedPreview()
  const isWide = useWideViewport()
  const artifactPanelOpen = useAssistantStore((s) => s.artifactPanelOpen)
  const hasArtifact = useAssistantStore((s) => s.artifact !== null)

  // Close any docked chat preview when leaving the page, so it doesn't
  // reappear as a full-screen modal on the next route (no dock host there).
  useEffect(() => () => { usePreviewStore.getState().closePreview() }, [])

  const { isReady, error: threadError, startNewChat } = useAssistantChatLifecycle(threadId)
  const handleNewChat = async () => {
    const newThreadId = await startNewChat()
    if (newThreadId) void navigate(`/app/assistant/${encodeURIComponent(newThreadId)}`)
  }

  const { messages, error, clearError, sendMessage } = useAssistantStore()
  const hasMessages = messages.length > 0
  const activeThreadId = useAssistantStore((s) => s.activeThreadId)
  const showConversation = isReady && (!threadId || activeThreadId === threadId)
  const unavailableError = threadError ?? (isReady && threadId && activeThreadId !== threadId ? "Conversation not found" : null)

  const handleRetry = () => {
    const lastUserMsg = [...messages].reverse().find((m) => m.role === 'user')
    if (lastUserMsg) {
      clearError()
      void sendMessage(lastUserMsg.content)
    }
  }

  const canRetry = messages.some((m) => m.role === 'user')
  const errorBanner = (
    <ChatErrorBanner error={error} onRetry={canRetry ? handleRetry : undefined} onDismiss={clearError} />
  )

  return (
    <div className="flex h-full overflow-hidden bg-card">
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-[320px]">
        {!showConversation && unavailableError && (
          <div role="alert" className="m-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            {unavailableError}
          </div>
        )}
        {/* One stable tree position for the composer across the empty↔has-messages
            flip: swapping whole layout branches remounts ChatInput and loses its
            local draft — including the SharePoint-connect stash restore, which is
            one-shot. Only wrapper classes may change with hasMessages.
            `min-h-0` is load-bearing: as a column flex item this wrapper defaults
            to `min-height: auto`, which floors it at the message list's content
            height, so a streaming answer grows it past the panel and pushes the
            composer below the fold.
            The empty state centres with auto margins (`mt-auto`/`mb-auto`), NOT
            `justify-center`: centred content that outgrows the pane — at browser
            zoom, or on a short window — splits its overflow evenly and puts the
            logo and heading above the top edge, unreachable because the row above
            is `overflow-hidden`. Auto margins collapse to 0 once free space runs
            out, so the block falls back to top-aligned and `overflow-y-auto`
            scrolls it. */}
        <div
          hidden={!showConversation}
          className={cn(
            "flex-1 flex flex-col min-h-0",
            !hasMessages && "items-center overflow-y-auto px-4 py-8",
            !showConversation && "hidden"
          )}
        >
          <div className={cn(hasMessages ? "flex-1 overflow-hidden" : "mt-auto")}>
            <ChatMessageArea />
          </div>
          <div className={cn(!hasMessages && "w-full mt-8 mb-auto")}>
            {errorBanner}
            <ChatInput isVisible={showConversation} />
          </div>
        </div>
      </div>

      {/* One right slot, three claimants (wide screens): the docked citation
          preview, else the quick-write document, else the history sidebar.
          A document opening evicts the preview (the store closes it), so the
          usual case is one claimant. The preview wins the tiebreak the other
          way round — it is the newer click — and the document returns to the
          slot when the preview closes. Narrow screens keep the sidebar; the
          document opens as a sheet instead. */}
      {showConversation && shouldDock ? (
        <ChatDocumentPanel />
      ) : showConversation && artifactPanelOpen && hasArtifact && isWide ? (
        <ArtifactPanel />
      ) : (
        <ChatHistorySidebar
          isExpanded={isHistoryExpanded}
          onExpandedChange={setIsHistoryExpanded}
          onSelectThread={(id) => void navigate(`/app/assistant/${encodeURIComponent(id)}`)}
          onNewChat={() => void handleNewChat()}
        />
      )}
    </div>
  )
}
